// Vega-Lite is a declarative JSON grammar for describing charts (marks + data encodings);
// Snowflake uses it to generate the chart specs returned by its query/notebook responses.
// This adapter converts those Vega-Lite specs into our library-neutral ChartSpec format.
import { ChartSpec, ChartType } from './chartSpec'

/** Vega-Lite mark type → our library-neutral chart type. Unlisted marks fall back to 'bar'. */
const MARK_TYPE_MAP: Record<string, ChartType> = {
  bar: 'bar',
  line: 'line',
  area: 'area',
  point: 'scatter',
  circle: 'scatter',
  square: 'scatter',
  tick: 'scatter',
  rule: 'line',
  trail: 'line',
  arc: 'pie',
  rect: 'heatmap'
}

/** Marks with no sane chart-shape mapping (maps, distribution summaries, plain annotations) —
 * skip rather than silently mis-rendering them via the bar-chart fallback. */
const UNSUPPORTED_MARKS = new Set(['geoshape', 'boxplot', 'text', 'errorband', 'errorbar'])

const getMarkType = (mark: unknown): string => {
  if (typeof mark === 'string') return mark
  if (mark && typeof mark === 'object') return String((mark as Record<string, unknown>).type || '')
  return ''
}

interface EncodingField {
  field: string
  title: string
  type: string
}

function getEncodingField(channel: any): EncodingField | null {
  if (!channel?.field) return null
  return {
    field: channel.field,
    title: channel.title || channel.axis?.title || channel.field,
    type: channel.type || ''
  }
}

// Temporal (date) fields commonly sit on an axis and hold category labels, same as nominal/ordinal.
const isCategoryType = (type: string): boolean =>
  type === 'nominal' || type === 'ordinal' || type === 'temporal'

/**
 * Parses Vega-Lite's common `({'A':'B',...})[datum.field]` calculate-transform idiom (used to
 * rename folded key values) into a plain lookup object, without evaluating arbitrary expressions.
 */
function parseCalculateLookup(calculate: string): Record<string, string> | null {
  const match = /^\(\{([\s\S]*)\}\)\[datum\.\w+\]$/.exec(calculate)
  if (!match) return null
  try {
    const parsed = JSON.parse(`{${match[1]}}`.replace(/'/g, '"'))
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

type CalcToken =
  | { type: 'num'; value: number }
  | { type: 'str'; value: string }
  | { type: 'field'; value: string }
  | { type: 'null' }
  | { type: 'bool'; value: boolean }
  | { type: 'op'; value: string }

const CALC_MULTI_CHAR_OPS = ['===', '!==', '==', '!=', '<=', '>=', '&&', '||']
const CALC_SINGLE_CHAR_OPS = ['<', '>', '+', '-', '*', '/', '?', ':', '(', ')', '!']

/** Tokenizes a `calculate` expression into the small JS-like grammar we support. Returns `null`
 * on any unrecognised syntax (e.g. a function call) so callers can bail out without evaluating. */
function tokenizeCalculate(expr: string): CalcToken[] | null {
  const tokens: CalcToken[] = []
  let i = 0
  while (i < expr.length) {
    const ch = expr[i]
    if (/\s/.test(ch)) {
      i++
      continue
    }
    if (ch === "'" || ch === '"') {
      let j = i + 1
      let value = ''
      while (j < expr.length && expr[j] !== ch) {
        value += expr[j]
        j++
      }
      if (expr[j] !== ch) return null
      tokens.push({ type: 'str', value })
      i = j + 1
      continue
    }
    const fieldMatch = /^datum\.\w+/.exec(expr.slice(i))
    if (fieldMatch) {
      tokens.push({ type: 'field', value: fieldMatch[0].slice(6) })
      i += fieldMatch[0].length
      continue
    }
    const numMatch = /^\d+(?:\.\d+)?/.exec(expr.slice(i))
    if (numMatch) {
      tokens.push({ type: 'num', value: Number(numMatch[0]) })
      i += numMatch[0].length
      continue
    }
    const wordMatch = /^[A-Za-z_]\w*/.exec(expr.slice(i))
    if (wordMatch) {
      const word = wordMatch[0]
      if (word === 'null') tokens.push({ type: 'null' })
      else if (word === 'true') tokens.push({ type: 'bool', value: true })
      else if (word === 'false') tokens.push({ type: 'bool', value: false })
      else return null // unknown identifier/function call — not a supported idiom
      i += word.length
      continue
    }
    const multiOp = CALC_MULTI_CHAR_OPS.find((op) => expr.startsWith(op, i))
    if (multiOp) {
      tokens.push({ type: 'op', value: multiOp })
      i += multiOp.length
      continue
    }
    if (CALC_SINGLE_CHAR_OPS.includes(ch)) {
      tokens.push({ type: 'op', value: ch })
      i++
      continue
    }
    return null // unrecognised character
  }
  return tokens
}

/**
 * Recursive-descent parser/evaluator for the tokenized grammar (lowest to highest precedence:
 * ternary, `||`, `&&`, equality, relational, `+`/`-`, `*`/`/`, unary `!`/`-`, primary). Evaluates
 * directly against `row` without `eval`/`Function`, so it's safe to run on arbitrary LLM output.
 */
class CalculateParser {
  private pos = 0
  constructor(private tokens: CalcToken[], private row: any) {}

  isAtEnd(): boolean {
    return this.pos >= this.tokens.length
  }

  private matchOp(...ops: string[]): string | null {
    const t = this.tokens[this.pos]
    if (t?.type === 'op' && ops.includes(t.value)) {
      this.pos++
      return t.value
    }
    return null
  }

  parseTernary(): unknown {
    const cond = this.parseLogicalOr()
    if (this.matchOp('?')) {
      const whenTrue = this.parseTernary()
      if (!this.matchOp(':')) throw new Error('expected :')
      const whenFalse = this.parseTernary()
      return cond ? whenTrue : whenFalse
    }
    return cond
  }

  private parseLogicalOr(): any {
    let left = this.parseLogicalAnd()
    while (this.matchOp('||')) left = left || this.parseLogicalAnd()
    return left
  }

  private parseLogicalAnd(): any {
    let left = this.parseEquality()
    while (this.matchOp('&&')) left = left && this.parseEquality()
    return left
  }

  private parseEquality(): any {
    let left = this.parseRelational()
    let op: string | null
    while ((op = this.matchOp('==', '===', '!=', '!=='))) {
      const right = this.parseRelational()
      if (op === '==') left = left == right
      else if (op === '===') left = left === right
      else if (op === '!=') left = left != right
      else left = left !== right
    }
    return left
  }

  private parseRelational(): any {
    let left = this.parseAdditive()
    let op: string | null
    while ((op = this.matchOp('<', '>', '<=', '>='))) {
      const right = this.parseAdditive()
      if (op === '<') left = left < right
      else if (op === '>') left = left > right
      else if (op === '<=') left = left <= right
      else left = left >= right
    }
    return left
  }

  private parseAdditive(): any {
    let left = this.parseMultiplicative()
    let op: string | null
    while ((op = this.matchOp('+', '-'))) {
      const right = this.parseMultiplicative()
      left = op === '+' ? left + right : left - right
    }
    return left
  }

  private parseMultiplicative(): any {
    let left = this.parseUnary()
    let op: string | null
    while ((op = this.matchOp('*', '/'))) {
      const right = this.parseUnary()
      left = op === '*' ? left * right : left / right
    }
    return left
  }

  private parseUnary(): any {
    if (this.matchOp('!')) return !this.parseUnary()
    if (this.matchOp('-')) return -(this.parseUnary() as number)
    return this.parsePrimary()
  }

  private parsePrimary(): any {
    const t = this.tokens[this.pos]
    if (!t) throw new Error('unexpected end of expression')
    if (t.type === 'num' || t.type === 'str' || t.type === 'bool') {
      this.pos++
      return t.value
    }
    if (t.type === 'null') {
      this.pos++
      return null
    }
    if (t.type === 'field') {
      this.pos++
      return this.row[t.value]
    }
    if (t.type === 'op' && t.value === '(') {
      this.pos++
      const value = this.parseTernary()
      if (!this.matchOp(')')) throw new Error('expected )')
      return value
    }
    throw new Error('unexpected token')
  }
}

/**
 * Evaluates a Vega-Lite `calculate` expression (a safe JS-like subset: datum field references,
 * string/number/null/boolean literals, comparison/equality/logical operators, arithmetic, unary
 * `!`/`-`, parentheses, and the ternary operator) against a row. Returns `undefined` for anything
 * outside this grammar (e.g. a function call) rather than throwing, so the caller can leave the
 * row unchanged.
 */
function evaluateCalculateExpression(calculate: string, row: any): unknown {
  const tokens = tokenizeCalculate(calculate.trim())
  if (!tokens || tokens.length === 0) return undefined
  try {
    const parser = new CalculateParser(tokens, row)
    const value = parser.parseTernary()
    return parser.isAtEnd() ? value : undefined
  } catch {
    return undefined
  }
}

/** Applies a `calculate` transform step, supporting the lookup idiom and the general expression
 * evaluator above; leaves rows unchanged when the expression can't be parsed. */
function applyCalculate(rows: any[], step: any): any[] {
  if (typeof step.as !== 'string' || typeof step.calculate !== 'string') return rows
  const lookup = parseCalculateLookup(step.calculate)
  const keyField = lookup ? /\[datum\.(\w+)\]$/.exec(step.calculate)?.[1] : undefined
  return rows.map((row) => {
    if (lookup && keyField) {
      return { ...row, [step.as]: lookup[row[keyField]] ?? row[keyField] }
    }
    const result = evaluateCalculateExpression(step.calculate, row)
    return result === undefined ? row : { ...row, [step.as]: result }
  })
}

/** Applies a `fold` transform step (reshapes wide measure columns into long `key`/`value` rows). */
function applyFold(rows: any[], step: any): any[] {
  const [keyField, valueField] = step.as?.length === 2 ? step.as : ['key', 'value']
  return rows.flatMap((row: any) =>
    step.fold.map((field: string) => ({ ...row, [keyField]: field, [valueField]: row[field] }))
  )
}

/** Applies an `aggregate` transform step (groups rows and reduces to sum/mean/count/min/max). */
function applyAggregate(rows: any[], step: any): any[] {
  const groupby: string[] = Array.isArray(step.groupby) ? step.groupby : []
  const aggregations: any[] = Array.isArray(step.aggregate) ? step.aggregate : []
  if (aggregations.length === 0) return rows

  const groups = new Map<string, any[]>()
  for (const row of rows) {
    const key = groupby.map((field) => String(row[field])).join('\u0000')
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(row)
  }

  return Array.from(groups.values()).map((groupRows) => {
    const result: Record<string, unknown> = {}
    groupby.forEach((field) => (result[field] = groupRows[0][field]))
    aggregations.forEach(({ field, op, as }: any) => {
      const nums = groupRows.map((r) => Number(r[field])).filter((n) => !Number.isNaN(n))
      switch (op) {
        case 'count':
          result[as] = groupRows.length
          break
        case 'mean':
        case 'average':
          result[as] = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
          break
        case 'min':
          result[as] = nums.length ? Math.min(...nums) : 0
          break
        case 'max':
          result[as] = nums.length ? Math.max(...nums) : 0
          break
        default:
          result[as] = nums.reduce((a, b) => a + b, 0)
      }
    })
    return result
  })
}

/** Applies a `window` transform step's `rank`/`row_number` op, honouring an optional `sort`. */
function applyWindowRank(rows: any[], step: any): any[] {
  const rankOp = (step.window || []).find((w: any) => w?.op === 'rank' || w?.op === 'row_number')
  if (!rankOp) return rows
  const asField = rankOp.as || 'rank'
  const sortSpec = Array.isArray(step.sort) ? step.sort[0] : null
  const sorted = [...rows]
  if (sortSpec?.field) {
    const direction = sortSpec.order === 'descending' ? -1 : 1
    sorted.sort((a, b) => (Number(a[sortSpec.field]) - Number(b[sortSpec.field])) * direction)
  }
  const rankByRow = new Map(sorted.map((row, i) => [row, i + 1]))
  return rows.map((row) => ({ ...row, [asField]: rankByRow.get(row) }))
}

/**
 * Applies a Vega-Lite `transform` pipeline, since `encoding` channels can reference fields (e.g.
 * `key`/`value` from a `fold`, or an aggregated measure) that only exist after these transforms
 * run — they're never present on the raw `data.values` rows. Unrecognised step shapes are left
 * as-is rather than aborting, so later, recognised steps still get a best-effort chance to run.
 */
function applyTransforms(values: any[], transform: any): any[] {
  if (!Array.isArray(transform)) return values
  return transform.reduce((rows: any[], step: any) => {
    if (!step || typeof step !== 'object') return rows
    if (Array.isArray(step.fold)) return applyFold(rows, step)
    if (Array.isArray(step.window)) return applyWindowRank(rows, step)
    if (Array.isArray(step.aggregate)) return applyAggregate(rows, step)
    if (typeof step.calculate === 'string') return applyCalculate(rows, step)
    return rows
  }, values)
}

/**
 * Converts a Vega-Lite chart spec (as sent by the backend's TOOL_CHART_CONTENT event) into our
 * library-neutral `ChartSpec`, so it can render through the existing `ChartRenderer` (ApexCharts)
 * pipeline already used by the `create_visualization` frontend tool. Returns `null` when the spec
 * doesn't match a shape we know how to convert; the caller should skip rendering in that case.
 */
export const convertVegaLiteToChartSpec = (vegaSpec: any): ChartSpec | null => {
  const rawValues: any[] = vegaSpec?.data?.values
  if (!Array.isArray(rawValues) || rawValues.length === 0) return null

  const rawMarkType = getMarkType(vegaSpec?.mark)
  if (UNSUPPORTED_MARKS.has(rawMarkType)) return null
  const markType = MARK_TYPE_MAP[rawMarkType] || 'bar'

  const values = applyTransforms(rawValues, vegaSpec?.transform)
  const encoding = vegaSpec?.encoding || {}
  const title =
    typeof vegaSpec?.title === 'string' ? vegaSpec.title : vegaSpec?.title?.text || undefined

  if (markType === 'pie') {
    const theta = getEncodingField(encoding.theta)
    const color = getEncodingField(encoding.color)
    if (!theta || !color) return null
    return {
      type: 'donut',
      title,
      categories: values.map((v) => String(v[color.field])),
      series: [{ name: theta.title, data: values.map((v) => Number(v[theta.field]) || 0) }]
    }
  }

  if (markType === 'heatmap') {
    // Heatmaps encode BOTH axes as categories; the quantitative value drives cell color, not x/y.
    const hx = getEncodingField(encoding.x)
    const hy = getEncodingField(encoding.y)
    const hColor = getEncodingField(encoding.color)
    if (!hx || !hy || !hColor) return null
    const categories = Array.from(new Set(values.map((v) => String(v[hx.field]))))
    const rows = Array.from(new Set(values.map((v) => String(v[hy.field]))))
    return {
      type: 'heatmap',
      title,
      categories,
      series: rows.map((rowLabel) => ({
        name: rowLabel,
        data: categories.map((category) => {
          const match = values.find(
            (v) => String(v[hx.field]) === category && String(v[hy.field]) === rowLabel
          )
          return match ? Number(match[hColor.field]) || 0 : 0
        })
      })),
      xAxisLabel: hx.title,
      yAxisLabel: hy.title
    }
  }

  const xField = getEncodingField(encoding.x)
  const yField = getEncodingField(encoding.y)
  if (!xField || !yField) return null

  // Whichever axis is nominal/ordinal holds the category labels; the other holds the values.
  const categoryField = isCategoryType(xField.type)
    ? xField
    : isCategoryType(yField.type)
    ? yField
    : null
  const valueField = categoryField === xField ? yField : categoryField === yField ? xField : null
  if (!categoryField || !valueField) return null

  const colorField = getEncodingField(encoding.color)
  const isGrouped = Boolean(colorField && colorField.field !== categoryField.field)
  // Vega-Lite only renders true side-by-side grouped bars when an xOffset/yOffset channel is
  // present; without one, `color` just recolors one bar per category (e.g. by rating), so stack
  // instead — otherwise Apex would split each category's row height across every color value,
  // leaving one thin sliver visible per row and the rest as invisible zero-length lanes.
  const hasOffsetChannel = Boolean(encoding.xOffset?.field || encoding.yOffset?.field)

  let categories: string[]
  let series: { name: string; data: number[] }[]

  if (isGrouped) {
    // Grouped chart: one series per distinct color/group value.
    categories = Array.from(new Set(values.map((v) => String(v[categoryField.field]))))
    const groups = Array.from(new Set(values.map((v) => String(v[colorField!.field]))))
    series = groups.map((group) => ({
      name: group,
      data: categories.map((category) => {
        const row = values.find(
          (v) =>
            String(v[categoryField.field]) === category && String(v[colorField!.field]) === group
        )
        return row ? Number(row[valueField.field]) || 0 : 0
      })
    }))
  } else {
    categories = values.map((v) => String(v[categoryField.field]))
    series = [{ name: valueField.title, data: values.map((v) => Number(v[valueField.field]) || 0) }]
  }

  return {
    type: markType,
    title,
    categories,
    series,
    xAxisLabel: categoryField.title,
    yAxisLabel: valueField.title,
    ...(isGrouped ? { stacked: !hasOffsetChannel } : {})
  }
}
